import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faSave,
    faSearch,
    faRefresh,
    faUsers,
    faSpinner,
    faExchangeAlt,
    faPlus,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faFilePdf,
    faPhone,
    faMapMarkerAlt,
    faHashtag,
    faUserPlus,
    faStore,
    faUser,
    faCalendar,
    faBuilding,
    faBriefcase,
    faMoneyBillWave,
    faUserTag,
    faEnvelope,
    faIdCard,
    faUniversity,
    faClock,
    faPercentage,
    faMinus,
    faArrowsAlt,
    faExpand,
    faCompress
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance (similar to customer list translator)
const createEmployeeListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Employee List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        employeeList: 'Employee List',
        allEmployees: 'Shop Employees',
        shopEmployees: 'Shop Employees',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search employee in shop',
        refresh: 'Refresh',
        loadingEmployees: 'Loading shop employees...',
        noEmployeesFound: 'No employees found in shop',
        serialNo: 'S.No',
        employeeName: 'Employee Name',
        phone: 'Phone',
        email: 'Email',
        position: 'Position',
        department: 'Department',
        joiningDate: 'Joining Date',
        salaryType: 'Salary Type',
        status: 'Status',
        actions: 'Actions',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete this employee from your shop?',
        deletedSuccess: 'Employee deleted successfully',
        deleteError: 'Error deleting employee',
        networkError: 'Network error occurred',
        noShopPermission: 'You are not assigned to any shop. Please contact administrator.',
        employeeId: 'Employee ID',
        name: 'Name',
        notProvided: 'Not provided',
        showing: 'Showing',
        of: 'of',
        employees: 'employees',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
        terminated: 'Terminated',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        phoneNumber: 'Phone Number',
        employeeIdFilter: 'Employee ID',
        positionFilter: 'Position',
        departmentFilter: 'Department',
        statusFilter: 'Status',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'PDF',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        fixed: 'Fixed',
        hourly: 'Hourly',
        commission: 'Commission',
        editEmployee: 'Edit Employee',
        updateEmployee: 'Update Employee',
        updating: 'Updating...',
        employeeUpdated: 'Employee updated successfully',
        updateError: 'Error updating employee',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the employee details below',
        nameRequired: 'Employee name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        dateInvalid: 'Please select a valid date',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this employee from your shop?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'All Shop Records',
        loadingUserData: 'Loading shop employee data...',
        noUserRecords: 'No employees added to your shop yet',
        addFirstEmployee: 'Add First Employee to Shop',
        businessReport: 'Shop Report',
        generatedBy: 'Generated by',
        employeeReport: 'Employee Report',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        allShopMembers: 'All Shop Members',
        teamEmployees: 'Team Employees',
        shopDashboard: 'Shop Dashboard',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        personalInfo: 'Personal Information',
        employmentInfo: 'Employment Information',
        salaryInfo: 'Salary Information',
        bankInfo: 'Bank Information',
        emergencyInfo: 'Emergency Contact',
        optional: 'Optional',
        required: 'Required',
        baseSalary: 'Base Salary',
        hourlyRate: 'Hourly Rate',
        commissionRate: 'Commission Rate',
        bankName: 'Bank Name',
        accountNumber: 'Account Number',
        ifscCode: 'IFSC Code',
        panNumber: 'PAN Number',
        aadharNumber: 'Aadhar Number',
        emergencyContact: 'Emergency Contact',
        emergencyPhone: 'Emergency Phone',
        address: 'Address',
        totalEmployees: 'Total Employees',
        reportFilters: 'Applied Filters'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const employeeListTranslator = createEmployeeListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 450, label: 'Narrow', icon: faMinus },
    medium: { width: 700, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 900, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

const EmployeeList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 700, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        employee_name: '',
        phone: '',
        email: '',
        address: '',
        position: '',
        department: '',
        joining_date: '',
        salary_type: 'fixed',
        base_salary: '',
        hourly_rate: '',
        commission_rate: '',
        bank_name: '',
        account_number: '',
        ifsc_code: '',
        pan_number: '',
        aadhar_number: '',
        emergency_contact: '',
        emergency_phone: '',
        status: 'active'
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Salary type options
    const salaryTypeOptions = [
        { value: 'fixed', label: 'Fixed' },
        { value: 'hourly', label: 'Hourly' },
        { value: 'commission', label: 'Commission' }
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'terminated', label: 'Terminated' }
    ];

    // Shop info state
    const [shopInfo, setShopInfo] = useState({
        shop_id: null,
        shop_name: '',
        shop_code: ''
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterBy, setFilterBy] = useState('all');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [employeeIdFilter, setEmployeeIdFilter] = useState('');
    const [positionFilter, setPositionFilter] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentEmployees = filteredEmployees.slice(startIndex, endIndex);

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 400,
            minHeight: 500,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsModalInitialized(false);
        }
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await employeeListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch employees on mount and when shop changes
    useEffect(() => {
        if (user && user.username) {
            fetchEmployees();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters when employees, search term, or filter type changes
    useEffect(() => {
        applyFilters();
    }, [employees, searchTerm, filterBy, phoneFilter, employeeIdFilter, positionFilter, departmentFilter, statusFilter, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchEmployees();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const fetchEmployees = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_employee_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setEmployees(data.data || []);

                if (data.shop_id) {
                    setShopInfo(prev => ({
                        ...prev,
                        shop_id: data.shop_id,
                        shop_name: data.shop_name || prev.shop_name,
                        shop_code: data.shop_code || prev.shop_code
                    }));
                }

                resetFilters();

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No employees found in your shop');
                }
            } else {
                if (data.message.includes('not assigned to any shop')) {
                    showToast('error', translations.noShopPermission || 'You are not assigned to any shop. Please contact administrator.');
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...employees];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(employee =>
                employee.employee_name?.toLowerCase().includes(term) ||
                employee.phone?.toLowerCase().includes(term) ||
                employee.email?.toLowerCase().includes(term) ||
                employee.position?.toLowerCase().includes(term) ||
                employee.department?.toLowerCase().includes(term) ||
                employee.employeeid?.toString().includes(term)
            );
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            filtered = filtered.filter(employee =>
                employee.phone?.toLowerCase().includes(phoneFilter.toLowerCase())
            );
        }

        if (filterBy === 'employeeId' && employeeIdFilter.trim()) {
            filtered = filtered.filter(employee =>
                employee.employeeid?.toString().toLowerCase().includes(employeeIdFilter.toLowerCase())
            );
        }

        if (filterBy === 'position' && positionFilter.trim()) {
            filtered = filtered.filter(employee =>
                employee.position?.toLowerCase().includes(positionFilter.toLowerCase())
            );
        }

        if (filterBy === 'department' && departmentFilter.trim()) {
            filtered = filtered.filter(employee =>
                employee.department?.toLowerCase().includes(departmentFilter.toLowerCase())
            );
        }

        if (filterBy === 'status' && statusFilter.trim()) {
            filtered = filtered.filter(employee =>
                employee.status === statusFilter
            );
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(employee => {
                if (!employee.joining_date) return false;
                const employeeDate = new Date(employee.joining_date);
                employeeDate.setHours(0, 0, 0, 0);
                return employeeDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(employee => {
                if (!employee.joining_date) return false;
                const employeeDate = new Date(employee.joining_date);
                return employeeDate <= end;
            });
        }

        setFilteredEmployees(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setPhoneFilter('');
        setEmployeeIdFilter('');
        setPositionFilter('');
        setDepartmentFilter('');
        setStatusFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleEdit = (employee) => {
        setSelectedEmployee(employee);
        setEditFormData({
            employee_name: employee.employee_name || '',
            phone: employee.phone || '',
            email: employee.email || '',
            address: employee.address || '',
            position: employee.position || '',
            department: employee.department || '',
            joining_date: employee.joining_date ? employee.joining_date.split('T')[0] : '',
            salary_type: employee.salary_type || 'fixed',
            base_salary: employee.base_salary || '',
            hourly_rate: employee.hourly_rate || '',
            commission_rate: employee.commission_rate || '',
            bank_name: employee.bank_name || '',
            account_number: employee.account_number || '',
            ifsc_code: employee.ifsc_code || '',
            pan_number: employee.pan_number || '',
            aadhar_number: employee.aadhar_number || '',
            emergency_contact: employee.emergency_contact || '',
            emergency_phone: employee.emergency_phone || '',
            status: employee.status || 'active'
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (employeeId) => {
        const employee = employees.find(e => e.employeeid === employeeId);
        if (!employee) {
            showToast('error', 'Employee not found');
            return;
        }

        setEmployeeToDelete(employeeId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!employeeToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete_employee',
                    employee_id: employeeToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Employee deleted successfully');
                fetchEmployees();
                setShowDeleteModal(false);
                setEmployeeToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting employee');
            }
        } catch (error) {
            console.error('Error deleting employee:', error);
            showToast('error', translations.deleteError || 'Error deleting employee');
        }
    };

    // Validate edit form
    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.employee_name.trim()) {
            newErrors.employee_name = translations.nameRequired || 'Employee name is required';
        } else if (editFormData.employee_name.trim().length < 2) {
            newErrors.employee_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (editFormData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(editFormData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }

        if (editFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }

        if (editFormData.joining_date && !/^\d{4}-\d{2}-\d{2}$/.test(editFormData.joining_date)) {
            newErrors.joining_date = translations.dateInvalid || 'Please select a valid date';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // FIXED: Changed employeeid to employee_id to match PHP backend
    const handleUpdateEmployee = async (e) => {
        e.preventDefault();

        if (!selectedEmployee || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/employees.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit_employee',
                    employee_id: selectedEmployee.employeeid,  // FIXED: Changed from 'employeeid' to 'employee_id'
                    ...editFormData,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.employeeUpdated || 'Employee updated successfully');
                fetchEmployees();
                setShowEditModal(false);
                setSelectedEmployee(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating employee');
            }
        } catch (error) {
            console.error('Error updating employee:', error);
            showToast('error', translations.updateError || 'Error updating employee');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Get status badge color
    const getStatusBadgeStyle = (status) => {
        const colors = {
            'active': { backgroundColor: '#10b981', color: '#ffffff' },
            'inactive': { backgroundColor: '#f59e0b', color: '#ffffff' },
            'terminated': { backgroundColor: '#ef4444', color: '#ffffff' }
        };
        return colors[status] || { backgroundColor: '#6b7280', color: '#ffffff' };
    };

    // Get salary type badge color
    const getSalaryTypeBadgeStyle = (type) => {
        const colors = {
            'fixed': { backgroundColor: '#3b82f6', color: '#ffffff' },
            'hourly': { backgroundColor: '#8b5cf6', color: '#ffffff' },
            'commission': { backgroundColor: '#ec4899', color: '#ffffff' }
        };
        return colors[type] || { backgroundColor: '#6b7280', color: '#ffffff' };
    };

    // Format date globally
    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Get current date
    const currentDateGlobal = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Export to PDF
    const handleExportPDF = async () => {
        if (filteredEmployees.length === 0) {
            showToast('error', translations.noEmployeesFound || 'No data to export!');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#6366f1';

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            const headerCellStyle = `
                padding: 10px 8px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: 9px; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);

                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);

                const dotSize = 0.3;
                const dotSpacing = 8;

                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const rowsPerPage = 20;
            let employeesToExport = [...filteredEmployees];
            const totalPagesCount = Math.ceil(employeesToExport.length / rowsPerPage);

            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${currentShop?.shop_name || translations.shopEmployees || 'Shop Employees'}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${translations.employeeReport || 'Employee Report'} - ${currentDateGlobal()}
                            ${currentShop ? ` | Shop: ${currentShop.name}` : ''}
                        </div>
                        <div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">
                            Generated by: ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                </div>
            `;

            const filterInfoHTML = (searchTerm || filterBy !== 'all' || startDate || endDate) ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${positionFilter ? ` | Position: ${positionFilter}` : ''}
                    ${departmentFilter ? ` | Department: ${departmentFilter}` : ''}
                    ${statusFilter ? ` | Status: ${statusFilter}` : ''}
                    ${startDate ? ` | From: ${startDate}` : ''}
                    ${endDate ? ` | To: ${endDate}` : ''}
                </div>
            ` : '';

            const summaryStatsHTML = `
                <div style="display: flex; justify-content: center; gap: 20px; margin: 10px 0; flex-wrap: wrap;">
                    <div style="background: ${hexToRgba(primaryColor, 0.05)}; padding: 5px 15px; border-radius: 20px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        <div style="font-size: 10px; color: #666; ${rtlTextStyles}">${translations.totalEmployees || 'Total Employees'}</div>
                        <div style="font-size: 14px; font-weight: 700; color: ${primaryColor};">${employeesToExport.length}</div>
                    </div>
                </div>
            `;

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: 8px; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.employeeName || 'Name'}</th>
                            <th style="${headerCellStyle}">${translations.phone || 'Phone'}</th>
                            <th style="${headerCellStyle}">${translations.position || 'Position'}</th>
                            <th style="${headerCellStyle}">${translations.department || 'Dept'}</th>
                            <th style="${headerCellStyle}">${translations.salaryType || 'Salary Type'}</th>
                            <th style="${headerCellStyle}">${translations.joiningDate || 'Joining'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.status || 'Status'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, employeesToExport.length);
                const pageEmployees = employeesToExport.slice(startRow, endRow);

                const tableRowsHTML = pageEmployees.map((employee, index) => {
                    const statusStyle = getStatusBadgeStyle(employee.status);
                    const salaryTypeStyle = getSalaryTypeBadgeStyle(employee.salary_type);

                    return `
                        <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                                <div style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 9px;">
                                    ${startRow + index + 1}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; font-weight: 500; color: #111827; border-bottom: 1px solid #f0f0f0; font-size: 8px; ${rtlTextStyles}">
                                <div style="font-weight: 600;">${employee.employee_name || '-'}</div>
                                <div style="font-size: 6px; color: #9ca3af;">ID: ${employee.employeeid}</div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${employee.phone || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${employee.position || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${employee.department || '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 6px; border-radius: 10px; background: ${salaryTypeStyle.backgroundColor}; color: ${salaryTypeStyle.color}; font-weight: 500; font-size: 7px;">
                                    ${translations[employee.salary_type] || employee.salary_type}
                                </div>
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; color: #6b7280; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                ${employee.joining_date ? formatDateGlobal(employee.joining_date) : '-'}
                            </td>
                            <td style="padding: 6px; border: none; text-align: center; border-bottom: 1px solid #f0f0f0; font-size: 8px;">
                                <div style="display: inline-block; padding: 2px 6px; border-radius: 10px; background: ${statusStyle.backgroundColor}; color: ${statusStyle.color}; font-weight: 500; font-size: 7px;">
                                    ${translations[employee.status] || employee.status}
                                </div>
                            </td>
                        </tr>
                    `;
                }).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';

                const pageHTML = `
                    <div style="width: 800px; padding: 15px 20px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        ${summaryStatsHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                            });
                        }
                    }
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPagesCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const fileName = `employees_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${currentDateGlobal()}.pdf`;
            pdf.save(fileName);

            showToast('success', `PDF exported successfully with ${employeesToExport.length} employee records across ${totalPagesCount} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    // Get today's date
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date based on start date
    useEffect(() => {
        if (startDate && endDateRef.current) {
            endDateRef.current.min = startDate;
        }
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) {
            startDateRef.current.max = today;
        }
        if (endDateRef.current) {
            endDateRef.current.max = today;
        }
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.employeeList || 'Employee List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allEmployees || 'My Employees'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="phone">{translations.phoneNumber || 'Phone Number'}</option>
                            <option value="employeeId">{translations.employeeIdFilter || 'Employee ID'}</option>
                            <option value="position">{translations.positionFilter || 'Position'}</option>
                            <option value="department">{translations.departmentFilter || 'Department'}</option>
                            <option value="status">{translations.statusFilter || 'Status'}</option>
                            <option value="date">{translations.dateRange || 'Joining Date'}</option>
                        </select>
                    </div>

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phoneNumber || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'employeeId' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={employeeIdFilter}
                                onChange={(e) => setEmployeeIdFilter(e.target.value)}
                                placeholder={translations.employeeIdFilter || 'Employee ID...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'position' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={positionFilter}
                                onChange={(e) => setPositionFilter(e.target.value)}
                                placeholder={translations.positionFilter || 'Position...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'department' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={departmentFilter}
                                onChange={(e) => setDepartmentFilter(e.target.value)}
                                placeholder={translations.departmentFilter || 'Department...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'status' && (
                        <div className="filter-group compact">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            >
                                <option value="">{translations.selectFilter || 'Select status...'}</option>
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {translations[option.label.toLowerCase()] || option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={startDateRef}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.startDate || 'Start Date'}
                                />
                            </div>
                            <div className="filter-group compact date-filter">
                                <input
                                    ref={endDateRef}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={getTodayDate()}
                                    className="filter-input compact date-input"
                                    title={translations.endDate || 'End Date'}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search employees...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchEmployees}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="refresh-btn-modern compact"
                            disabled={loading || filteredEmployees.length === 0 || pdfLoading}
                            title={translations.exportToPDF || 'Export PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop employee data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view employees'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="name-column centered-cell">{translations.employeeName || 'Employee Name'}</th>
                                            <th className="phone-column centered-cell">{translations.phone || 'Phone'}</th>
                                            <th className="position-column centered-cell">{translations.position || 'Position'}</th>
                                            <th className="department-column centered-cell">{translations.department || 'Department'}</th>
                                            <th className="salary-type-column centered-cell">{translations.salaryType || 'Salary Type'}</th>
                                            <th className="joining-date-column centered-cell">{translations.joiningDate || 'Joining Date'}</th>
                                            <th className="status-column centered-cell">{translations.status || 'Status'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentEmployees.map((employee, index) => {
                                            const statusStyle = getStatusBadgeStyle(employee.status);
                                            const salaryTypeStyle = getSalaryTypeBadgeStyle(employee.salary_type);

                                            return (
                                                <tr key={employee.employeeid}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="name-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" />
                                                            <span className="cell-text">{employee.employee_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="phone-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            {employee.phone ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faPhone} className="cell-icon" />
                                                                    <span className="cell-text">{employee.phone}</span>
                                                                </>
                                                            ) : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="position-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faBriefcase} className="cell-icon" />
                                                            <span className="cell-text">{employee.position || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="department-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faBuilding} className="cell-icon" />
                                                            <span className="cell-text">{employee.department || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="salary-type-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faMoneyBillWave} className="cell-icon" />
                                                            <span
                                                                className="salary-type-badge"
                                                                style={{
                                                                    backgroundColor: salaryTypeStyle.backgroundColor,
                                                                    color: salaryTypeStyle.color,
                                                                    padding: '4px 8px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                {translations[employee.salary_type] || employee.salary_type}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="joining-date-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            {employee.joining_date ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                                    <span className="cell-text">{formatDateGlobal(employee.joining_date)}</span>
                                                                </>
                                                            ) : '-'}
                                                        </div>
                                                    </td>
                                                    <td className="status-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span
                                                                className="status-badge"
                                                                style={{
                                                                    backgroundColor: statusStyle.backgroundColor,
                                                                    color: statusStyle.color,
                                                                    padding: '4px 8px',
                                                                    borderRadius: '12px',
                                                                    fontSize: '11px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                {translations[employee.status] || employee.status}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button
                                                                onClick={() => handleEdit(employee)}
                                                                className="action-btn-modern edit-btn-modern"
                                                                title={translations.edit || 'Edit'}
                                                            >
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(employee.employeeid)}
                                                                className="action-btn-modern delete-btn-modern"
                                                                title={translations.delete || 'Delete'}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                      </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredEmployees.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredEmployees.length)} {translations.of || 'of'} {filteredEmployees.length} {translations.employees || 'employees'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE, Does NOT close on outside click */}
            {showEditModal && selectedEmployee && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '400px',
                            maxWidth: '95vw',
                            maxHeight: '85vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faUser} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editEmployee || 'Edit Employee'}</h3>
                                <span className="employee-number-badge">#{selectedEmployee.employeeid}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={mode.label}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)} type="button">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateEmployee} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="form-grid edit-form-grid">
                                    {/* Employee Name */}
                                    <div className={`form-group ${editFormErrors.employee_name ? 'has-error' : ''}`}>
                                        <label htmlFor="employee_name">{translations.employeeName || 'Employee Name'} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="employee_name"
                                            name="employee_name"
                                            value={editFormData.employee_name}
                                            onChange={handleEditFormChange}
                                            required
                                            className={editFormErrors.employee_name ? 'error' : ''}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {editFormErrors.employee_name && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.employee_name}
                                            </div>
                                        )}
                                    </div>

                                    {/* Phone */}
                                    <div className={`form-group ${editFormErrors.phone ? 'has-error' : ''}`}>
                                        <label htmlFor="phone">{translations.phone || 'Phone'}</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={editFormData.phone}
                                            onChange={handleEditFormChange}
                                            className={editFormErrors.phone ? 'error' : ''}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        {editFormErrors.phone && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.phone}
                                            </div>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div className={`form-group ${editFormErrors.email ? 'has-error' : ''}`}>
                                        <label htmlFor="email">{translations.email || 'Email'}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={editFormData.email}
                                            onChange={handleEditFormChange}
                                            className={editFormErrors.email ? 'error' : ''}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        {editFormErrors.email && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.email}
                                            </div>
                                        )}
                                    </div>

                                    {/* Joining Date */}
                                    <div className={`form-group ${editFormErrors.joining_date ? 'has-error' : ''}`}>
                                        <label htmlFor="joining_date">{translations.joiningDate || 'Joining Date'}</label>
                                        <input
                                            type="date"
                                            id="joining_date"
                                            name="joining_date"
                                            value={editFormData.joining_date}
                                            onChange={handleEditFormChange}
                                            className={editFormErrors.joining_date ? 'error' : ''}
                                            disabled={editLoading}
                                            dir="ltr"
                                        />
                                        {editFormErrors.joining_date && (
                                            <div className="error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.joining_date}
                                            </div>
                                        )}
                                    </div>

                                    {/* Position */}
                                    <div className="form-group">
                                        <label htmlFor="position">{translations.position || 'Position'}</label>
                                        <input
                                            type="text"
                                            id="position"
                                            name="position"
                                            value={editFormData.position}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    {/* Department */}
                                    <div className="form-group">
                                        <label htmlFor="department">{translations.department || 'Department'}</label>
                                        <input
                                            type="text"
                                            id="department"
                                            name="department"
                                            value={editFormData.department}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    {/* Salary Type */}
                                    <div className="form-group">
                                        <label htmlFor="salary_type">{translations.salaryType || 'Salary Type'}</label>
                                        <select
                                            id="salary_type"
                                            name="salary_type"
                                            value={editFormData.salary_type}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {salaryTypeOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {translations[option.label.toLowerCase()] || option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Conditional Salary Fields */}
                                    {editFormData.salary_type === 'fixed' && (
                                        <div className="form-group">
                                            <label htmlFor="base_salary">{translations.baseSalary || 'Base Salary'}</label>
                                            <input
                                                type="number"
                                                id="base_salary"
                                                name="base_salary"
                                                value={editFormData.base_salary}
                                                onChange={handleEditFormChange}
                                                step="0.01"
                                                min="0"
                                                disabled={editLoading}
                                                dir="ltr"
                                            />
                                        </div>
                                    )}

                                    {editFormData.salary_type === 'hourly' && (
                                        <div className="form-group">
                                            <label htmlFor="hourly_rate">{translations.hourlyRate || 'Hourly Rate'}</label>
                                            <input
                                                type="number"
                                                id="hourly_rate"
                                                name="hourly_rate"
                                                value={editFormData.hourly_rate}
                                                onChange={handleEditFormChange}
                                                step="0.01"
                                                min="0"
                                                disabled={editLoading}
                                                dir="ltr"
                                            />
                                        </div>
                                    )}

                                    {editFormData.salary_type === 'commission' && (
                                        <div className="form-group">
                                            <label htmlFor="commission_rate">{translations.commissionRate || 'Commission Rate (%)'}</label>
                                            <input
                                                type="number"
                                                id="commission_rate"
                                                name="commission_rate"
                                                value={editFormData.commission_rate}
                                                onChange={handleEditFormChange}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                disabled={editLoading}
                                                dir="ltr"
                                            />
                                        </div>
                                    )}

                                    {/* Status */}
                                    <div className="form-group">
                                        <label htmlFor="status">{translations.status || 'Status'}</label>
                                        <select
                                            id="status"
                                            name="status"
                                            value={editFormData.status}
                                            onChange={handleEditFormChange}
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            {statusOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {translations[option.label.toLowerCase()] || option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Address */}
                                    <div className="form-group full-width">
                                        <label htmlFor="address">{translations.address || 'Address'}</label>
                                        <textarea
                                            id="address"
                                            name="address"
                                            value={editFormData.address}
                                            onChange={handleEditFormChange}
                                            rows="3"
                                            disabled={editLoading}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </div>

                                {/* Employee Info Footer */}
                                <div className="edit-employee-info">
                                    <p><strong>{translations.employeeId || 'Employee ID'}:</strong> #{selectedEmployee.employeeid}</p>
                                    <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedEmployee.username}</p>
                                    <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)} disabled={editLoading}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (
                                        <><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>
                                    ) : (
                                        <><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>
                                    )}
                                </button>
                            </div>
                        </form>

                        {/* Loading Overlay for Edit */}
                        {editLoading && (
                            <div className="loading-overlay edit-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>{translations.updating || 'Updating...'}</p>
                                    <p className="loading-subtext">Please wait while we update employee data</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Does NOT close on outside click */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this employee?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeList;